import { schema as agreementSchema } from '@/components/patient-acknowledgment-and-agreement/schema';
import { toast } from '@/components/ui/toast/use-toast';
import { useFileUpload } from '@/components/upload-file/useFileUpload';
import { getParam } from '@/lib/utils';
import { store } from '@/store/store';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import * as z from 'zod';

const formSchema = toTypedSchema(
    z
        .object({
            items: z
                .array(z.string())
                .refine((value) => value.some((item) => item), {
                    message: "You have to select at least one item.",
                }),
            symptoms: z.array(z.string()),
            note: z.string().optional(),
            drugs: z.array(z.string()).optional(),
            uploadedFiles: z.array(z.object({
                name: z.string(),
                id: z.string(),
            })).optional(),
        })
        .merge(agreementSchema)
        .refine(
            ({ items, note }) => {
                const patientOnlyWantsRenewal = items.length === 1 && items[0] === "prescription-renewal";
                const patientHasNote = !!note?.length;
                
                return patientOnlyWantsRenewal || patientHasNote;
            },
            {
                message: "Required",
                path: ["note"],
            }
    )
        .refine(
            ({ symptoms, items }) => {
                const isNewSymptoms = items.some((item) => item === 'new-symptoms');
                return !isNewSymptoms || symptoms.length > 0;
            },
            {
                message: "Make sure to select at least one symptom.",
                path: ["symptoms"],
            }
        )
    .refine(
        ({ items, uploadedFiles, symptoms }) => {
            const isNewSymptoms = items.some((item) => item === 'new-symptoms');
            const isSkinProblem = symptoms.some((symptom) => symptom === 'skin-problem');
            const userUploadedFiles = !!uploadedFiles && uploadedFiles?.length > 0;
            return isNewSymptoms && isSkinProblem ? userUploadedFiles : true;
        },
        {
            message: "Make sure to upload a picture of your skin problem.",
            path: ["uploadedFiles"],
        }
    )
);

export const useReasonForm = () => {
    const router = useRouter();
    const route = useRoute();
    const loader = ref(false);
    
    const { handleSubmit, values, setValues } = useForm({
        validationSchema: formSchema,
        initialValues: {
            items: [],
            symptoms: [],
            drugs: [],
            uploadedFiles: [],
        },
    });

    const fileUpload = useFileUpload();

    // Initialize from localStorage
    const initializeFromStorage = () => {
        let localDetails: any = localStorage.getItem('appointment');
        localDetails = JSON.parse(localDetails);
        if (localDetails) {
            store.appointment = JSON.parse(JSON.stringify(localDetails));
        }
        if (localDetails?.note) {
            setValues({
                note: localDetails.note.split('Patient] ')[1],
            });
        }
    };

    const onSubmit = handleSubmit((values) => {
        if (fileUpload.fileLoader.value) {
            toast({
                title: "Please wait for file upload to complete",
                variant: 'destructive'
            });
            return;
        } 

        store.appointment.note = values.note || '';
        store.appointment.appointmentTags = values.items;
        store.appointment.symptoms = values.symptoms;
        store.appointment.drugs = values.drugs || [];

        store.appointment.fileIds = values.uploadedFiles?.map((f) => f.id) || [];

        loader.value = true;

        setTimeout(() => {
            loader.value = false;
            router.push({ name: 'doctors', params: { domain: getParam(route) } })
        }, 300)
    });

    watch(() => values.note, (val) => {
        store.appointment.note = '[This note has been sent by the Patient] ' + val;
        localStorage.setItem('appointment', JSON.stringify(store.appointment));
    });

    onMounted(() => {
        initializeFromStorage();
    });

    return {
        loader,
        values,
        onSubmit,
        fileUpload,
    };
};
